import * as React from 'react';
import { Card, Table, Spin, Icon } from 'antd';
import moment from 'moment';
import Chart from '../../common/chart/Chart';
import { fetchReportChart } from '../../../api/reporting';
import { scalarValue, toPieChartData, toSeriesChartData } from './reporting.chartData';

const loadingIndicator = <Icon type="loading" style={{ fontSize: 36 }} spin />;

interface ReportChartCardProps {
  reportKey: string;
  chart: { key: string; title: string; visualisationType: string; width: number; formData?: object };
  filters: object;
}

// Strict ISO-8601 datetime so we never touch reference numbers / plain strings.
const ISO_DATETIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;
const NUMERIC = /^-?\d+(\.\d+)?$/;

const isMoneyColumn = (name: string) => name.indexOf('$') !== -1;

const formatCell = (value: any, columnName: string) => {
  if (value === null || value === undefined || value === '') {
    return value;
  }
  if (typeof value === 'string' && ISO_DATETIME.test(value)) {
    return moment(value).format('DD MMM YYYY, h:mm A');
  }
  const isNumeric = typeof value === 'number' || (typeof value === 'string' && NUMERIC.test(value));
  if (isNumeric && isMoneyColumn(columnName)) {
    return Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 });
  }
  return value;
};

const centered: React.CSSProperties = { minHeight: 120, display: 'flex', alignItems: 'center', justifyContent: 'center' };

// Visualisations whose data is a {label, value...} series adapted for @shipsy/chartify.
const SERIES_VIS_TYPES = ['bar', 'line', 'area'];

// One chart failing to render must not blank the whole report page.
class ChartErrorBoundary extends React.Component<{ children: React.ReactNode }, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  render() {
    if (this.state.failed) {
      return <div style={centered}>Could not display this chart</div>;
    }
    return this.props.children;
  }
}

const ReportChartCard = ({ reportKey, chart, filters }: ReportChartCardProps) => {
  const [loading, setLoading] = React.useState(true);
  const [data, setData] = React.useState<any>(null);
  const [error, setError] = React.useState<string>('');
  const [visible, setVisible] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);

  // Lazy load: only fetch a chart's data once its card scrolls near the viewport,
  // so opening the report doesn't fire all charts' heavy queries at once.
  React.useEffect(() => {
    if (visible) {
      return undefined;
    }
    const node = containerRef.current;
    if (!node || typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '250px' }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [visible]);

  React.useEffect(() => {
    if (!visible) {
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError('');
    fetchReportChart(reportKey, chart.key, filters)
      .then((res: any) => {
        if (cancelled) {
          return;
        }
        if (res.isSuccess) {
          setData(res.data);
        } else {
          setError('Could not load this chart');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [visible, reportKey, chart.key, JSON.stringify(filters)]);

  const renderBody = () => {
    if (!visible || loading) {
      return (
        <div style={{ minHeight: 220, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Spin indicator={loadingIndicator} tip="Loading…" />
        </div>
      );
    }
    if (error) {
      return <div style={centered}>{error}</div>;
    }
    if (!data || !data.rows || !data.rows.length) {
      return <div style={centered}>No data</div>;
    }
    if (chart.visualisationType === 'table') {
      return (
        <Table
          size="small"
          bordered
          scroll={{ x: 'max-content' }}
          pagination={{ pageSize: 25, size: 'small', showSizeChanger: false }}
          columns={data.columns.map((c: any) => ({
            title: c.name,
            dataIndex: c.id,
            key: c.id,
            ellipsis: true,
            render: (value: any) => formatCell(value, c.name),
          }))}
          dataSource={data.rows.map((row: any[], i: number) =>
            data.columns.reduce((acc: any, c: any, idx: number) => ({ ...acc, [c.id]: row[idx] }), { key: i })
          )}
        />
      );
    }
    if (chart.visualisationType === 'scalar') {
      const value = scalarValue(data);
      return (
        <div style={{ ...centered, fontSize: 36, fontWeight: 600 }}>
          {value === null ? '-' : value.toLocaleString()}
        </div>
      );
    }
    const isPie = chart.visualisationType === 'pie';
    if (isPie || SERIES_VIS_TYPES.indexOf(chart.visualisationType) !== -1) {
      return (
        <div style={{ height: 320 }}>
          <Chart
            visType={chart.visualisationType}
            formData={{ visualisation_type: chart.visualisationType, ...chart.formData }}
            chartData={isPie ? toPieChartData(data) : toSeriesChartData(data, chart.visualisationType)}
            width="100%"
            height={320}
          />
        </div>
      );
    }
    return <div style={centered}>Unsupported chart type</div>;
  };

  return (
    <div ref={containerRef}>
      <Card title={chart.title} style={{ marginBottom: 16 }}>
        <ChartErrorBoundary key={`${chart.key}:${JSON.stringify(filters)}`}>{renderBody()}</ChartErrorBoundary>
      </Card>
    </div>
  );
};

export default ReportChartCard;
